export const TURNSTILE_TOKEN_HEADER = "x-turnstile-token";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Must match the `action` the backend expects for each endpoint (`TURNSTILE_ACTIONS` in coloca-nest). */
export type TurnstileAction = "login" | "register" | "forgot_password";

export interface TurnstileRenderOptions {
  sitekey: string;
  action: TurnstileAction;
  appearance: "always" | "execute" | "interaction-only";
  theme: "auto" | "light" | "dark";
  size: "normal" | "flexible" | "compact";
  language: string;
  callback: (token: string) => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
  "error-callback": (code: string) => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function turnstileHeaders(token: string | null | undefined): Record<string, string> {
  return token ? { [TURNSTILE_TOKEN_HEADER]: token } : {};
}

export function turnstileLanguage(language: string): string {
  if (language === "pt") return "pt-BR";
  if (language === "en") return "en";
  return "es";
}

let scriptPromise: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile_missing"));
    script.onerror = () => reject(new Error("turnstile_unreachable"));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // A blocked or failed download must be retryable on the next mount, not cached as a failure.
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}
